import { useEffect, useState } from 'react';

import { Cart, CartEntryType, VariantMatrix } from '@telialt/models/src/EshopModel';
import { selectProductInCartCode } from '@telialt/utils/src/eshopSelectors';
import { findLeafByCode, getFirstInStockLeaf, getFirstLeaf, toPreparedVariations } from '@telialt/utils/src/eshopUtils';
import { isSSR, removeQueryParam } from '@telialt/utils/src/helpers';

import { VARIANT_QUERY_PARAM } from '../../constants';
import { useAddProductMutation } from '../../hooks/mutations/useAddProductMutation';
import { useCartQuery } from '../../hooks/queries/useCartQuery';

const getInitialProductCode = (cart: Cart, variantMatrix: VariantMatrix[]): string => {
    const productInCartCode = selectProductInCartCode(cart);

    const preparedVariations = toPreparedVariations(variantMatrix);
    const previousLeaf = findLeafByCode(preparedVariations, productInCartCode);
    const firstInStockLeaf = getFirstInStockLeaf(preparedVariations);
    const firstLeaf = getFirstLeaf(preparedVariations);

    const newProductCodeToBeAdded = (firstInStockLeaf || firstLeaf)?.code;

    return previousLeaf?.code || newProductCodeToBeAdded;
};

const findVariantByCode = (variantMatrix: VariantMatrix[], code: string) => {
    const preparedVariations = toPreparedVariations(variantMatrix);

    return findLeafByCode(preparedVariations, code);
};

export const useAddInitialProductToCart = (variantMatrix: VariantMatrix[]) => {
    const { cart } = useCartQuery();
    const { addProduct } = useAddProductMutation();
    const [variantInQuery, setVariantInQuery] = useState(
        (!isSSR() && new URLSearchParams(window.location.search)?.get(VARIANT_QUERY_PARAM)) || undefined,
    );
    const productInCartCode = selectProductInCartCode(cart);
    const hasNoData = !cart || !variantMatrix;

    const queryValidVariantCode = findVariantByCode(variantMatrix, variantInQuery)?.code;
    const defaultVariantCode = getInitialProductCode(cart, variantMatrix);

    useEffect(() => {
        if (hasNoData) {
            return;
        }
        if (queryValidVariantCode && queryValidVariantCode !== productInCartCode) {
            addProduct({ code: queryValidVariantCode, entryType: CartEntryType.PurchasableProduct });
        } else if (!productInCartCode && defaultVariantCode) {
            addProduct({ code: defaultVariantCode, entryType: CartEntryType.PurchasableProduct });
        }
    }, [addProduct, defaultVariantCode, queryValidVariantCode, hasNoData, productInCartCode]);

    useEffect(() => {
        if (hasNoData) {
            return;
        }
        if (
            variantInQuery &&
            (queryValidVariantCode === productInCartCode || variantInQuery !== queryValidVariantCode)
        ) {
            removeQueryParam(VARIANT_QUERY_PARAM);
            setVariantInQuery(undefined);
        }
    }, [queryValidVariantCode, hasNoData, productInCartCode, variantInQuery]);
};
